<template>
  <div class="a-login-view fullscreen row justify-center items-center">
    <q-card color="white" text-color="black" class="row no-wrap">
      <div class="col full-height relative-position">
        <div class="diagonal-bg" />
        <img
          :src="$app.logoLight"
          alt="Autengo Logo"
          width="270"
          class="logo"
        >
      </div>
      <form
        class="
          col-right
          column
          no-wrap
          relative-position
          gutter-y-md
          q-ma-md q-py-md q-px-lg
        "
        @submit.prevent="login"
      >
        <div class="wrapper" v-if="$app.whitelabel === 'eln'">
          <div class="box col-right column no-wrap relative-position gutter-y-md">
            <div class="q-title">Willkommen bei Autengo!</div>
            <div class="q-caption text-faded q-mt-lg" v-if="loginIsRunning">
              Bitte warten Sie einen Moment, Sie werden eingeloggt.
            </div>
            <q-spinner color="primary" size="50px" class="q-my-xl self-center" v-if="loginIsRunning" />
            <q-alert type="negative" v-if="error">{{ error }}</q-alert>
          </div>
        </div>
        <div class="wrapper" v-else>
          <div
            class="box col-right column no-wrap relative-position gutter-y-sm"
            :class="{ 'box-transition': needsPassword }"
          >
            <div class="q-title">Willkommen zurück!</div>
            <div class="q-caption text-faded" v-if="!error">
              Bitte loggen Sie sich ein, um fortzufahren.
            </div>
            <q-alert type="negative" v-if="error">{{ error }}</q-alert>

            <q-field label="E-Mail Adresse" orientation="vertical">
              <q-input
                autofocus
                placeholder="z. B. max.musterman@email.com"
                name="username"
                v-model.trim="email"
              />
            </q-field>

            <q-field label="Passwort" orientation="vertical">
              <q-input
                type="password"
                placeholder="z. B. a$2RJA@xV3"
                name="password"
                v-model="password"
              />
            </q-field>
            <router-link
              :to="'/forgot-password'"
              tag="a"
              class="q-caption text-faded self-end q-mt-xs"
            >
              Passwort vergessen?
            </router-link>

            <q-field>
              <q-btn
                type="submit"
                label="Login"
                color="primary"
                class="full-width q-py-md"
                no-caps
                :loading="loginIsRunning"
              >
                <span slot="loading">
                  <span class="absolute-center move-left">
                    <q-spinner />
                  </span>
                  Login
                </span>
              </q-btn>
            </q-field>

            <div>
              <div class="relative-position">
                <q-item-separator />
                <div
                  class="absolute"
                  style="top: 50%; left: 50%; transform: translate(-50%, -50%);"
                >
                  <span class="q-body-1 q-px-sm bg-white">
                    ODER
                  </span>
                </div>
              </div>
            </div>

            <q-field>
              <q-btn
                label="Login mit Passkey"
                icon="key"
                color="positive"
                class="full-width q-py-md"
                :loading="loginIsRunning"
                @click="loginWithPasskey"
              >
                <span slot="loading">
                  <span class="absolute-center move-left">
                    <q-spinner />
                  </span>
                  Login
                </span>
              </q-btn>
            </q-field>

            <div class="text-center">
              <div class="q-caption text-faded q-mb-sm">Neu bei autengo?</div>
              <a
                href="https://www.autengo.com/"
                class="q-subheading text-primary text-bold"
              >Hier Konto erstellen</a>
            </div>
          </div>

          <div class="col-right column no-wrap relative-position gutter-y-md">
            <div class="q-title">
              <q-btn
                round
                icon="arrow_back"
                color="grey-4"
                text-color="faded"
                size="xs"
                class="no-shadow back-btn q-mr-sm"
                :class="{ 'btn-transition': needsPassword }"
                @click="goBack"
              />Erstellen Sie ein Passwort!
            </div>
            <div class="q-caption text-faded" v-if="!error">
              Um fortzufahren, erstellen Sie bitte Ihr eigenes Passwort und
              klicken danach auf Passwort ändern
            </div>
            <q-alert type="negative" v-if="error">{{ error }}</q-alert>

            <q-field
              :error="$v.newPassword.$error"
              :error-label="$getErrorMessage($v.newPassword)"
              label="Neues Passwort"
              orientation="vertical"
            >
              <q-input
                type="password"
                placeholder="z. B. a$2RJA@xV3"
                v-model="newPassword"
                name="newPassword"
              />
            </q-field>
            <q-field
              :error="$v.repeatedPassword.$error"
              :error-label="$getErrorMessage($v.repeatedPassword)"
              label="Passwort wiederholen"
              orientation="vertical"
            >
              <q-input
                placeholder="z. B. a$2RJA@xV3"
                type="password"
                v-model="repeatedPassword"
                name="repeatedPassword"
              />
            </q-field>
            <div>
              <q-btn
                @click="changePassword"
                label="Passwort ändern"
                color="primary"
                class="full-width q-py-md"
                no-caps
                :loading="loginIsRunning"
              >
                <span slot="loading">
                  <span class="absolute-center move-left">
                    <q-spinner />
                  </span>
                  Passwort ändern
                </span>
              </q-btn>
            </div>
          </div>
        </div>
      </form>
    </q-card>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { startAuthentication } from '@simplewebauthn/browser';
import ValidationMixin from '@/plugins/validationMixin';
import { mustSatisfyIfConditionSatisifed, required, minLength } from '@/plugins/validator';
import VerifyOtpLoginModal from '@/components/security/VerifyOtpLoginModal.vue';

export default {
  // eslint-disable-next-line vue/match-component-file-name
  name: 'LoginView',
  mixins: [ValidationMixin],
  data() {
    return {
      email: '',
      password: '',
      loginIsRunning: false,
      needsPassword: false,
      newPassword: null,
      repeatedPassword: null,
    };
  },
  computed: {
    ...mapState('auth', ['error']),
  },
  async created() {
    const { query } = this.$route;

    // Optional check if already logged in
    if (this.$store.getters['auth/loggedIn']) {
      this.$router.push(query.next || '/');
      return;
    }

    if (query.error) {
      let error = '';
      switch (query.error) {
        case 'AuthTokenMissing':
        case 'AuthTokenInvalid':
        case 'AuthTokenExpired':
        case 'UserNotFound':
        case 'UserNotActive':
          error = `Ein Fehler ist aufgetreten! Bitte kontaktieren Sie den Support. Fehler: ${query.error}`;
          break;
        default:
          error = 'Ein unbekannter Fehler ist aufgetreten! Bitte kontaktieren Sie den Support.';
          break;
      }

      this.$store.commit('auth/VALIDATION_FAILED', error);
    } else if (query.token) {
      this.login({ token: query.token });
    }
  },

  validations() {
    return {
      newPassword: {
        required,
        minLength: minLength(6),
      },
      repeatedPassword: {
        required,
        mustSatisfy: mustSatisfyIfConditionSatisifed('Eingaben müssen übereinstimmen')({
          fields: ['newPassword', 'repeatedPassword'],
          condition: (newPassword, repeatedPassword) => newPassword !== repeatedPassword,
          validation: () => false,
        }, () => this),
        minLength: minLength(6),
      },
    };
  },

  methods: {
    async login({ token }) {
      this.loginIsRunning = true;

      const payload = token ? { token } : {
        email: this.email.replace(/\s+$/g, ''),
        password: this.password,
      };

      let user = await this.$store.dispatch('auth/login', payload);

      if (user === 'otp') {
        user = await this.$q.modal(VerifyOtpLoginModal, { payload }).catch(() => null);
      }

      if (!user) {
        this.loginIsRunning = false;
        return;
      }

      if (user.needsPassword) {
        this.needsPassword = true;
        this.loginIsRunning = false;
        return;
      }

      this.$router.push(this.$route.query.next || '/');
      this.loginIsRunning = false;
    },
    async loginWithPasskey() {
      if (
        !window.PublicKeyCredential
        || typeof window.PublicKeyCredential !== 'function'
      ) {
        this.$notifyUserInteraction.warn('Passkeys werden von diesem Browser leider nicht unterstützt!');
        return;
      }

      try {
        this.loginIsRunning = true;

        const { data } = await this.$axios.get('/v2/auth/login/passkey/options');

        const authentication = await startAuthentication({
          optionsJSON: data.passkeyOptions,
        });

        const user = await this.$store.dispatch('auth/loginWithPasskey', {
          authenticationId: data.authenticationId,
          authentication,
        });

        if (!user) {
          this.loginIsRunning = false;
          return;
        }

        this.$router.push(this.$route.query.next || '/');
        this.loginIsRunning = false;
      } catch (error) {
        console.error(error);
        this.loginIsRunning = false;
      }
    },
    changePassword() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }
      this.loginIsRunning = true;
      this.$store.dispatch('auth/createPassword', this.repeatedPassword).then(() => {
        this.$notifyUserInteraction.success('Passwort erfolgreich erstellt');
        this.$router.push('/');
      });
    },
    goBack() {
      this.needsPassword = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
@import theme

login-width = 1100px;
login-height = 650px;

.a-login-view {
  background-image: linear-gradient(to top, white, transparent), url('@/assets/autengo-login-bg.jpeg');
  background-size: cover;

  .q-card {
    max-width: login-width;
    max-height: login-height;
    width: 100%;
    height: 100%;
    box-shadow: 0px 6px 20px 5px rgba(13, 51, 32, 0.1);
    border-radius: 8px;
    border: none;
    overflow: hidden;

    @media (max-width: login-width) and (max-height: login-height) {
      border-radius: 0;
    }

    @media (max-width: 780px) {
      width: 483px;
    }

    @media (max-width: 483px) {
      border-radius: 0;
      max-height: 100%;
      width: 100%;
    }
  }

  .col-right {
    flex: 0 0 450px;
    overflow-y: auto;

    @media (max-width: 483px) {
      flex: 1 1 100%;
    }
  }

  .diagonal-bg {
    position: absolute;
    left: 0;
    top: -20%;
    right: 0;
    bottom: -20%;
    overflow: hidden;
    border-radius: 0;
    background-color: $primary;
    background-image: radial-gradient(circle farthest-side at 100% 0, $primary, #091841);
    -webkit-transform: skew(
      0deg,
      -9deg
    );
    -ms-transform: skew(0deg, -9deg);
    transform: skew(
      0deg,
      -9deg
    );
  }

  .logo {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    /* margin-right: auto; */
    top: 0;
    bottom: 0;

    @media (max-width: 780px) {
      display: none;
    }
  }

  .wrapper {
    position: relative;
    overflow: hidden;
    width: 400px;
    height: 100%;
  }

  .box {
    position: fixed;
    background: white;
    transition: 1s;
    z-index: 1000;
    width: 400px;
    transform: translateX(0);
    opacity: 1;
  }

  .box-transition {
    transition: 1s;
    transform: translateX(-500px);
    opacity: 0;
  }

  .back-btn {
    overflow: hidden;
    transition-delay: 0.4s;
  }

  .back-btn:hover {
    transition-delay: 0s;
    transform: rotate(360deg);
  }

  .btn-transition {
    overflow: hidden;
    transition-duration: 0.8s;
    transition-property: transform;
    transform: rotate(180deg);
  }

  .back-btn >>>.q-icon {
    font-size: 2em !important;
  }
}
</style>
